import React, { useState } from 'react'
import {
  ProductQuantityStatusEnum,
  useBasket,
  useConfigState,
  useGlobalLabels,
} from '@sentecacommerce-theme/lib'

import styled, { css } from 'styled-components'
import { IconArrowDown, IconArrowRight, IconBasket } from '../Icons'
import { Button } from '../Button'
import { Typography } from '../Typography'
import { AddToBasketCheckМark } from '../AddToBasketCheckmark'

export const BasketButton = ({
  productId,
  variantId,
  offerId,
  maxQuantity,
  selectedOptions,
  type,
  onClick: parentOnClick,
  canOrder,
}: BasketButtonProps) => {
  const [quantity, setQuantity] = useState(1)
  const config = useConfigState()
  const [showAnimation, setShowAnimation] = useState(false)
  const { buttonLabels, errorMessages } = useGlobalLabels()
  const isIOS =
    typeof window !== 'undefined'
      ? window?.navigator?.userAgent?.match(/iPhone|iPad|iPod/i)
      : null

  const onQuantityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setQuantity(+e.target.value)
  }

  const {
    addToBasket,
    isExisting: isInBasket,
    quantityInBasket,
    status,
  } = useBasket({
    productId,
    variantId,
    offerId: offerId as string,
    quantity: maxQuantity,
    selectedOptions,
  })

  const onClick = (e: any) => {
    e.preventDefault()
    if (showAnimation) return

    addToBasket(quantity)
    setShowAnimation?.(true)
    if (e.target.dataset.borderRadius === 'small') {
      setTimeout(() => setShowAnimation?.(false), 1400)
    }
  }

  if (type === 'icon') {
    if (status !== ProductQuantityStatusEnum.Available || !canOrder) {
      return null
    }

    return (
      <Con
        onClick={parentOnClick || onClick}
        type="button"
        aria-label={buttonLabels?.ariaLabel}
        style={{
          pointerEvents: isInBasket ? 'none' : 'all',
        }}
      >
        {showAnimation || isInBasket ? (
          <CheckmarkWrapperListingPage>
            <AddToBasketCheckМark />
          </CheckmarkWrapperListingPage>
        ) : (
          <>
            <Count>{isInBasket && quantityInBasket}</Count>
            <StyledBasketIcon />
          </>
        )}
      </Con>
    )
  }

  if (!offerId) return <OutOfStock>{errorMessages?.outOfStock}</OutOfStock>

  const btnClassNames = 'd-flex justify-content-center align-items-center'

  if (isInBasket && !showAnimation) {
    return (
      <AddToBasketButtonsContainer>
        <Button
          variant="green"
          fullWidth
          link="/basket"
          borderRadius="small"
          icon={<IconArrowRight />}
          iconPosition="right"
          style={{ minHeight: 44 }}
          aria-label={buttonLabels?.ariaLabel}
          className={btnClassNames}
        >
          {buttonLabels?.alreadyInBasket}
        </Button>
      </AddToBasketButtonsContainer>
    )
  }

  if (status !== ProductQuantityStatusEnum.Available || !canOrder) {
    return (
      <AddToBasketButtonsContainer>
        <Button
          variant="disabled"
          fullWidth
          borderRadius="small"
          iconPosition="right"
          disabled
          style={{ minHeight: 44 }}
          aria-label={buttonLabels?.ariaLabel}
          className={btnClassNames}
        >
          {errorMessages?.notAvailable}
        </Button>
      </AddToBasketButtonsContainer>
    )
  }

  return (
    <ProductPageSquareBtnContainer>
      <CountInput isIOS={isIOS} value={quantity} onChange={onQuantityChange}>
        {Array.from({ length: maxQuantity }, (_, val) => (
          <option key={`quantity__${val + 1}`} value={val + 1}>
            {val + 1}
          </option>
        ))}
      </CountInput>
      <Con
        onClick={parentOnClick || onClick}
        type="button"
        aria-label={buttonLabels?.ariaLabel}
      >
        <Button
          variant="green"
          fullWidth
          as="span"
          borderRadius="small"
          style={{ minHeight: '100%', lineHeight: 'normal' }}
          aria-label={buttonLabels?.ariaLabel}
          className={btnClassNames}
        >
          {showAnimation ? (
            <CheckmarkWrapperProductPage>
              <AddToBasketCheckМark color={config.palette.white} />
            </CheckmarkWrapperProductPage>
          ) : (
            buttonLabels?.add.toUpperCase()
          )}
        </Button>
      </Con>
      <CountArrowContainer>
        <IconArrowDown />
      </CountArrowContainer>
    </ProductPageSquareBtnContainer>
  )
}

type BasketButtonProps = {
  productId: string
  variantId: string
  offerId?: string
  maxQuantity: number
  selectedOptions?: any[]
  onAddCallback?: (() => void) | undefined
  setShowAnimation?: React.Dispatch<React.SetStateAction<boolean>>
  type: 'icon' | 'normal'
  onClick?: () => void
  canOrder?: boolean
}

const CheckmarkWrapperListingPage = styled.div`
  position: relative;
  left: 0.2rem;
  width: 3.1rem;
  height: 3.1rem;
  bottom: -0.2rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.md.max}) {
    width: 3.7rem;
    height: 3.7rem;
  }
`

const CheckmarkWrapperProductPage = styled.div`
  position: absolute;
  top: 0.6rem;
  right: 45%;
  width: 3rem;
  height: 3rem;
`

const Con = styled.button.attrs({
  className: 'd-flex align-items-end justify-content-end  p-0',
})`
  background: none !important;
  border: 0 !important;
  opacity: 1 !important;
  position: relative;
`

const AddToBasketButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: stretch;
  position: relative;

  & span {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  & button {
    width: 100%;
  }
`

const ProductPageSquareBtnContainer = styled.div.attrs({
  className: '',
})`
  width: 100%;
  display: flex;
  align-items: stretch;
  position: relative;

  & span {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  & button {
    width: 100%;
  }
`

const OutOfStock = styled.span.attrs({
  className: 'd-flex align-items-center mr-4',
})`
  color: ${({ theme }) => theme.palette.danger};
  min-height: 4.4rem;
`

const Count = styled.span.attrs({
  className: 'd-block',
})`
  position: absolute;
  font-size: 1.1rem;
  font-weight: bold;
  left: 0.3rem;
  top: 0.7rem;

  width: 100%;
  text-align: center;
`

const CountInput = styled(Typography).attrs({
  as: 'select',
  variant: 6,
  color: 'secondary',
})<any>`
  padding: 0 3.4rem 0 2rem;
  border: 0;
  outline: 0;
  margin-right: 0.1rem;
  appearance: none;
  border-top-left-radius: 0.4rem;
  border-bottom-left-radius: 0.4rem;

  // Did  this because it presents an issue on apple devices
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;

  background-color: ${({ theme }) => theme.palette.silverTree};
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.palette.silverTree};
  }

  & option {
    color: ${({ theme }) => theme.palette.greyTitle};
    background-color: ${({ theme }) => theme.palette.secondary};
  }
`

const CountArrowContainer = styled.div`
  position: absolute;
  left: 3.7rem;
  top: calc(50% - 1.3rem);
  color: ${({ theme }) => theme.palette.secondary};
  pointer-events: none;
  & svg {
    width: 2rem;
    height: 2rem;
  }
`

const StyledBasketIcon = styled(IconBasket)`
  width: 2.6rem;
  height: 2.6rem;
  color: ${({ theme }) => theme.palette.silverTree};

  @media (min-width: ${({ theme }) => theme.breakpoints.md.max}) {
    width: 3rem;
    height: 3rem;
  }
`
